import { useEffect, useState, useCallback } from 'react';
import { Database, BrainCircuit, Activity, ShieldCheck, Link2, Unlink, FolderTree, Terminal, Upload, RefreshCw, Download, Folder, ArrowLeft, FolderPlus, X, Trash2 } from 'lucide-react';

interface BillingStatus {
  plan_id: string;
  tokens_used: number;
  tokens_saved: number;
  tokens_quota: number;
  sessions_active: number;
  sessions_quota: number;
}

interface ProjectInfo {
  name: string;
  path: string;
  branch: string;
  status: string;
  uploadedAt?: string;
}

interface CloudProject {
  id: string;
  name: string;
  description: string;
  vector_count: number;
  file_count: number;
  created_at?: string;
  updated_at?: string;
}

function LocalAgentPairingCard() {
  const [onlineAgents, setOnlineAgents] = useState<string[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const [projects, setProjects] = useState<ProjectInfo[]>([]);
  const [uploadingPath, setUploadingPath] = useState<string | null>(null);
  const [uploadResults, setUploadResults] = useState<Record<string, 'ok' | 'error'>>({});

  const [cloudProjects, setCloudProjects] = useState<CloudProject[]>([]);
  const [loadingCloud, setLoadingCloud] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Remote Browser state
  const [showBrowser, setShowBrowser] = useState(false);
  const [browserPath, setBrowserPath] = useState('');
  const [browserItems, setBrowserItems] = useState<{name: string, path: string, isDir: boolean}[]>([]);
  const [loadingBrowser, setLoadingBrowser] = useState(false);

  const fetchCloudProjects = useCallback(async () => {
    const token = localStorage.getItem('eric_token');
    if (!token) return;
    setLoadingCloud(true);
    try {
      const res = await fetch('https://mec.wowerix.com/v1/projects', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCloudProjects(data);
      }
    } catch { /* ignore */ }
    setLoadingCloud(false);
  }, []);

  useEffect(() => {
    fetchCloudProjects();
  }, [fetchCloudProjects]);

  // Poll Cloudflare AgentBridge for online agents
  const pollAgents = useCallback(async () => {
    const token = localStorage.getItem('eric_token');
    if (!token) return;
    try {
      const res = await fetch('https://mec.wowerix.com/api/bridge/agents', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const agents = await res.json();
        setOnlineAgents(agents);
        if (agents.length > 0 && !selectedAgent) {
          setSelectedAgent(agents[0]); // auto select first
        } else if (selectedAgent && !agents.includes(selectedAgent)) {
          setSelectedAgent(null);
          setProjects([]);
        }
      }
    } catch {}
  }, [selectedAgent]);

  // Poll selected agent for its projects
  const pollAgentProjects = useCallback(async () => {
    if (!selectedAgent) return;
    const token = localStorage.getItem('eric_token');
    try {
      const res = await fetch(`https://mec.wowerix.com/api/bridge/execute?agentId=${selectedAgent}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ action: 'getProjects' })
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) setProjects(data);
      }
    } catch {}
  }, [selectedAgent]);

  useEffect(() => {
    pollAgents();
    const interval = setInterval(pollAgents, 5000);
    return () => clearInterval(interval);
  }, [pollAgents]);

  useEffect(() => {
    pollAgentProjects();
    const interval = setInterval(pollAgentProjects, 3000);
    return () => clearInterval(interval);
  }, [pollAgentProjects]);

  // Sync state to Ericoder via Erix MEC backend
  useEffect(() => {
    if (projects.length > 0) {
      const token = localStorage.getItem('eric_token');
      if (token) {
        fetch('/v1/admin/sync-ericoder', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ workspace: projects[0].name })
        }).catch(() => {});
      }
    }
  }, [projects]);

  const handleDisconnect = async () => {
    setSelectedAgent(null);
    setProjects([]);
  };

  const handleUpload = async (proj: ProjectInfo) => {
    if (!selectedAgent) return;
    setUploadingPath(proj.path);
    setUploadResults(prev => ({ ...prev, [proj.path]: undefined as any }));
    try {
      const res = await fetch(`https://mec.wowerix.com/api/bridge/execute?agentId=${selectedAgent}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('eric_token')}` },
        body: JSON.stringify({ 
          action: 'uploadToMEC',
          payload: { path: proj.path, token: localStorage.getItem('eric_token') || '' }
        }),
      });
      const data = await res.json() as { status?: string; data?: any; error?: string; ok?: boolean };
      if (res.ok && (data.ok === true || data.status === 'success' || data.status === 'upload_started' || (data.data && data.data.ok))) {
        setUploadResults(prev => ({ ...prev, [proj.path]: 'ok' }));
      } else {
        console.error('Upload failed response:', data);
        setUploadResults(prev => ({ ...prev, [proj.path]: 'error' }));
      }
    } catch (err) {
      console.error('Upload exception:', err);
      setUploadResults(prev => ({ ...prev, [proj.path]: 'error' }));
    } finally {
      setUploadingPath(null);
      pollAgentProjects(); 
      fetchCloudProjects();
    }
  };

  const handleCloseProject = async (_proj: ProjectInfo) => {
    alert("Not implemented via Cloud yet.");
  };

  const handleDeleteCloudProject = async (projectId: string, projectName: string) => {
    if (!confirm(`Bạn có chắc muốn xóa dự án "${projectName}" trên Cloud RAG Context không?`)) return;
    const token = localStorage.getItem('eric_token');
    try {
      const res = await fetch(`https://mec.wowerix.com/v1/projects/${projectId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchCloudProjects();
      } else {
        alert('Xóa dự án thất bại');
      }
    } catch {
      alert('Lỗi kết nối khi xóa dự án');
    }
  };

  const loadBrowserPath = async (path: string) => {
    if (!selectedAgent) return;
    setLoadingBrowser(true);
    try {
      const res = await fetch(`https://mec.wowerix.com/api/bridge/execute?agentId=${selectedAgent}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('eric_token')}` },
        body: JSON.stringify({ action: 'listDir', payload: { path } })
      });
      if (res.ok) {
        const data = await res.json();
        setBrowserItems(data || []);
        setBrowserPath(path);
      }
    } catch {}
    setLoadingBrowser(false);
  };

  const handleAddWorkspace = async (path: string) => {
    if (!selectedAgent) return;
    try {
      await fetch(`https://mec.wowerix.com/api/bridge/execute?agentId=${selectedAgent}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('eric_token')}` },
        body: JSON.stringify({ action: 'addProject', payload: { path } })
      });
      setShowBrowser(false);
      pollAgentProjects();
    } catch {}
  };

  const _handleSelectWorkspace = () => {
    if (!selectedAgent) {
      alert("Please select an agent first.");
      return;
    }
    setShowBrowser(true);
    loadBrowserPath(""); // Load root
  };

  const isAgentOnline = onlineAgents.length > 0;
  const isConnected  = selectedAgent !== null;

  return (
    <div className="glass-panel" style={{
      padding: '24px',
      background: isConnected
        ? 'linear-gradient(135deg, rgba(16,185,129,0.08) 0%, rgba(6,182,212,0.05) 100%)'
        : 'linear-gradient(135deg, rgba(56,189,248,0.08) 0%, rgba(30,41,59,0.4) 100%)',
      border: isConnected ? '1px solid rgba(16,185,129,0.4)' : '1px solid rgba(56,189,248,0.3)',
      boxShadow: isConnected ? '0 0 25px rgba(16,185,129,0.15)' : '0 0 20px rgba(56,189,248,0.1)',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <div style={{ background: isConnected ? 'rgba(16,185,129,0.2)' : 'rgba(56,189,248,0.2)', padding: '8px', borderRadius: '8px', color: isConnected ? 'var(--accent-green)' : 'var(--accent-cyan)' }}>
              {isConnected ? <ShieldCheck size={22} /> : <Link2 size={22} />}
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: 700, margin: 0, color: isConnected ? '#4ade80' : 'var(--text-primary)' }}>
              {isConnected
                ? `🚀 Remote ComputeUse Agent: ${selectedAgent}`
                : isAgentOnline
                  ? '🔋 Agents Online — Please select an agent'
                  : '🔌 No Remote ComputeUse Agents connected'}
            </h3>
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: 0, maxWidth: '650px', lineHeight: '1.5' }}>
            {isConnected
              ? `Viewing remote workspace on ${selectedAgent}.`
              : isAgentOnline
                ? `Found ${onlineAgents.length} agents online. Select one below.`
                : <span>Run Eric_MEC_ComputeUse.exe on your machines to remotely connect them.</span>}
          </p>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <select
            value={selectedAgent || ''}
            onChange={(e) => setSelectedAgent(e.target.value)}
            style={{
              background: 'rgba(15,23,42,0.8)',
              border: '1px solid rgba(56,189,248,0.3)',
              borderRadius: '8px',
              color: '#38bdf8',
              padding: '8px 12px',
              fontSize: '13px',
              fontWeight: 600,
              outline: 'none',
              cursor: 'pointer'
            }}
          >
            <option value="" disabled>{isAgentOnline ? 'Select an Agent' : 'No agents online'}</option>
            {onlineAgents.map(a => <option key={a} value={a}>{a}</option>)}
          </select>

          {isConnected && (
            <button
              onClick={handleDisconnect}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: '8px', color: '#f87171', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}
            >
              <Unlink size={16} /> Disconnect
            </button>
          )}
        </div>
      </div>

      {/* ── Browse Workspace Projects ── */}
      <div style={{ marginTop: '22px', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '18px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, fontSize: '15px', color: '#e2e8f0' }}>
            <FolderTree size={18} style={{ color: 'var(--accent-cyan)' }} />
            Browse Remote Workspace Projects
            <button
              onClick={_handleSelectWorkspace}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)', borderRadius: '6px', color: '#10b981', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
            >
              <FolderPlus size={13} /> Chọn thư mục
            </button>
            <button
              onClick={pollAgentProjects}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.3)', borderRadius: '6px', color: '#38bdf8', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
            >
              <RefreshCw size={13} /> Refresh
            </button>
          </div>
        </div>

        <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '12px' }}>
          {projects.length === 0
            ? isConnected
              ? 'Folder is empty or no projects added to the agent.'
              : 'Select an agent to view its projects.'
            : `${projects.length} project(s) found on ${selectedAgent}.`}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '10px', marginBottom: '16px' }}>
          {projects.length > 0 ? projects.map((proj) => {
            const uploadResult = uploadResults[proj.path];
            const isUploading = uploadingPath === proj.path;
            return (
              <div
                key={proj.path}
                style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '14px', background: 'rgba(30,41,59,0.6)', border: '1px solid rgba(56,189,248,0.25)', borderRadius: '10px' }}
              >
                {/* Project header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                  <div
                    onClick={() => {
                      const el = document.getElementById('dashTerminalStream');
                      if (el) el.innerText = `PS ${proj.path}> Connecting...\n[+] Workspace: ${proj.path}\n[+] Branch: ${proj.branch} | Status: ${proj.status}\n[Ready] Stream open for Ericoder / Antigravity IDE.`;
                    }}
                    style={{ cursor: 'pointer', flex: 1, minWidth: 0 }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 700, fontSize: '14px', color: '#38bdf8' }}>
                      📁 {proj.name}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: '2px', wordBreak: 'break-all' }}>{proj.path}</div>
                  </div>
                  <button
                    onClick={() => handleCloseProject(proj)}
                    title="Close project"
                    style={{
                      flexShrink: 0, width: '22px', height: '22px', padding: 0,
                      background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
                      borderRadius: '4px', color: '#f87171', cursor: 'pointer',
                      fontSize: '12px', fontWeight: 700, lineHeight: 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all .2s',
                    }}
                    onMouseEnter={e => { (e.target as HTMLElement).style.background = 'rgba(239,68,68,0.3)'; }}
                    onMouseLeave={e => { (e.target as HTMLElement).style.background = 'rgba(239,68,68,0.12)'; }}
                  >✕</button>
                </div>

                {/* Branch + Status */}
                <div style={{ display: 'flex', gap: '8px', fontSize: '11px', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '8px' }}>
                  <span style={{ color: '#a855f7' }}>🌿 {proj.branch}</span>
                  <span style={{ color: '#4ade80' }}>● {proj.status}</span>
                  {proj.uploadedAt && (
                    <span style={{ color: '#94a3b8', marginLeft: 'auto' }}>
                      ☁ {new Date(proj.uploadedAt).toLocaleTimeString()}
                    </span>
                  )}
                </div>

                {/* Upload status badge */}
                {uploadResult && (
                  <div style={{
                    fontSize: '11px', padding: '4px 8px', borderRadius: '4px', fontWeight: 600,
                    background: uploadResult === 'ok' ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
                    color: uploadResult === 'ok' ? '#4ade80' : '#f87171',
                    border: `1px solid ${uploadResult === 'ok' ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
                  }}>
                    {uploadResult === 'ok' ? '✅ Uploaded to Erix MEC' : '❌ Upload failed'}
                  </div>
                )}

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {/* Upload to Erix MEC */}
                  <button
                    onClick={() => handleUpload(proj)}
                    disabled={isUploading}
                    style={{
                      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                      padding: '6px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 700, cursor: isUploading ? 'wait' : 'pointer',
                      background: isUploading ? 'rgba(56,189,248,0.08)' : 'linear-gradient(135deg, rgba(56,189,248,0.2), rgba(99,102,241,0.2))',
                      border: '1px solid rgba(56,189,248,0.4)', color: '#38bdf8',
                      opacity: isUploading ? 0.7 : 1,
                    }}
                  >
                    <Upload size={11} />
                    {isUploading ? 'Uploading...' : 'Upload to Erix MEC'}
                  </button>

                  {/* Launch Ericoder IDE */}
                  <button
                    onClick={() => window.open(`https://coder.wowerix.com/#token=${encodeURIComponent(localStorage.getItem('eric_token') || '')}&workspace=${encodeURIComponent(proj.path)}`, '_blank')}
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 10px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: '1px solid #fbbf24', borderRadius: '6px', color: '#fff', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
                  >
                    🚀 Ericoder IDE
                  </button>
                </div>
              </div>
            );
          }) : (
            <div style={{ color: 'var(--text-muted)', fontSize: '13px', gridColumn: '1 / -1', padding: '12px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              {isAgentOnline ? (
                <>
                  <span style={{ color: '#4ade80' }}>●</span>
                  Agent online — Click "Browse Workspace" in the desktop app to scan a project folder.
                </>
              ) : (
                <>
                  <span style={{ color: '#f87171' }}>●</span>
                  No projects found. Launch Erix MEC ComputeUse desktop app first.
                </>
              )}
            </div>
          )}
        </div>

        {/* ── ERIX MEC DRIVE (Cloud RAG Context) ── */}
        <div style={{ marginTop: '22px', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '18px', marginBottom: '22px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, fontSize: '15px', color: '#e2e8f0' }}>
              <Database size={18} style={{ color: '#a855f7' }} />
              ERIX MEC DRIVE (Cloud RAG Context)
            </div>
            <button
              onClick={fetchCloudProjects}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.3)', borderRadius: '6px', color: '#c084fc', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
            >
              <RefreshCw size={13} /> Refresh Cloud
            </button>
          </div>

          <input
            type="text"
            placeholder="🔍 Tìm kiếm codebase trên Cloud..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 14px',
              borderRadius: '8px',
              border: '1px solid rgba(168,85,247,0.3)',
              background: 'rgba(15,23,42,0.6)',
              color: 'var(--text-primary)',
              marginBottom: '16px',
              fontSize: '13px',
              outline: 'none'
            }}
          />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '10px' }}>
            {loadingCloud ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '13px', padding: '12px 0' }}>Loading cloud projects...</div>
            ) : (cloudProjects || []).filter(p => p && (p.name || '').toLowerCase().includes((searchQuery || '').toLowerCase())).length > 0 ? (
              (cloudProjects || []).filter(p => p && (p.name || '').toLowerCase().includes((searchQuery || '').toLowerCase())).map((proj) => (
                <div
                  key={proj.id}
                  style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '14px', background: 'rgba(30,41,59,0.6)', border: '1px solid rgba(168,85,247,0.25)', borderRadius: '10px' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 700, fontSize: '14px', color: '#c084fc' }}>
                    ☁️ {proj.name || 'Untitled Project'}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px', wordBreak: 'break-all' }}>
                    {proj.description || 'No description'}
                  </div>
                  
                  <div style={{ display: 'flex', gap: '8px', fontSize: '11px', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '8px' }}>
                    <span style={{ color: '#4ade80' }}>● Indexed: {proj.vector_count} chunks</span>
                    <span style={{ color: '#94a3b8', marginLeft: 'auto' }}>
                      {new Date(proj.updated_at || proj.created_at || Date.now()).toLocaleDateString()}
                    </span>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
                    <button
                      onClick={() => window.open(`https://coder.wowerix.com/#token=${encodeURIComponent(localStorage.getItem('eric_token') || '')}&workspace=${encodeURIComponent(proj.name)}`, '_blank')}
                      style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', padding: '6px 10px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: '1px solid #fbbf24', borderRadius: '6px', color: '#fff', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
                    >
                      🚀 Mở trong Ericoder
                    </button>

                    <a
                      href={`https://mec.wowerix.com/v1/projects/${proj.id}/download?token=${encodeURIComponent(localStorage.getItem('eric_token') || '')}`}
                      download
                      style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 10px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: '#e2e8f0', fontSize: '11px', fontWeight: 700, cursor: 'pointer', textDecoration: 'none' }}
                    >
                      <Download size={14} />
                      Tải về
                    </a>

                    <button
                      onClick={() => handleDeleteCloudProject(proj.id, proj.name)}
                      title="Xóa dự án khỏi Cloud RAG Context"
                      style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 10px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', color: '#f87171', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
                    >
                      <Trash2 size={14} />
                      Xóa
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ color: 'var(--text-muted)', fontSize: '13px', gridColumn: '1 / -1', padding: '12px 0' }}>
                Trống (Chưa có Project nào trên Cloud)
              </div>
            )}
          </div>
        </div>

        {/* Terminal Stream */}
        <div style={{ background: '#090d16', border: '1px solid #1e293b', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', background: '#111827', borderBottom: '1px solid #1e293b', fontSize: '12px', color: '#94a3b8' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Terminal size={14} style={{ color: 'var(--accent-cyan)' }} />
              <span>Terminal Stream Output (PowerShell Exec Wrapper)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: isConnected ? '#4ade80' : '#f87171' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: isConnected ? '#4ade80' : '#f87171', display: 'inline-block' }}></span>
              {isConnected ? 'Live Tunnel Connected' : 'Tunnel Offline'}
            </div>
          </div>
          <div id="dashTerminalStream" style={{ padding: '14px', fontFamily: "'Consolas', 'Courier New', monospace", fontSize: '13px', color: '#38bdf8', lineHeight: '1.5', minHeight: '120px', maxHeight: '200px', overflowY: 'auto', whiteSpace: 'pre-wrap' }}>
            {`PS D:\\ERI CODE> Ready. Click any workspace folder above to stream local execution context...`}
          </div>
        </div>

        {/* Authorized Frontend Connections */}
        <div style={{ marginTop: '22px', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, fontSize: '15px', color: '#e2e8f0', marginBottom: '12px' }}>
            <span style={{ color: '#f59e0b' }}>🔐</span> Authorized Frontend Connections
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: 'rgba(15,23,42,0.8)', border: '1px solid rgba(56,189,248,0.3)', borderRadius: '8px', flexWrap: 'wrap', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: 'rgba(56,189,248,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>🚀</div>
                <div>
                  <div style={{ fontWeight: 700, color: '#fff', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    Ericoder AI IDE Workspace <span style={{ fontSize: '11px', padding: '2px 6px', background: 'rgba(74,222,128,0.2)', color: '#4ade80', borderRadius: '12px' }}>🟢 Active Stream</span>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>Permissions: Read/Write & Terminal Execution | Shared Session: eric@wowerix.com</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '12px', color: '#94a3b8' }}>Last Active: Just now</span>
                <button
                  onClick={() => alert('Thu hồi quyền truy cập thành công! Luồng Local Tunnel tới Ericoder đã được đóng lại.')}
                  style={{ padding: '6px 12px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: '6px', color: '#f87171', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Revoke Tunnel
                </button>
              </div>
            </div>

            {onlineAgents.map(a => (
              <div key={a} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: 'rgba(15,23,42,0.8)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '8px', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: 'rgba(16,185,129,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>🤖</div>
                  <div>
                    <div style={{ fontWeight: 700, color: '#fff', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      Remote Agent ({a}) <span style={{ fontSize: '11px', padding: '2px 6px', background: 'rgba(74,222,128,0.2)', color: '#4ade80', borderRadius: '12px' }}>🟢 WebSocket Tunnel</span>
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>Permissions: Full File System & Terminal Control</div>
                  </div>
                </div>
                <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end' }}>
                  <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Status: {a === selectedAgent ? 'Selected' : 'Online'}</div>
                  <button 
                    onClick={async () => {
                      if (window.confirm('Cảnh báo: Bạn sắp thu hồi quyền truy cập của TẤT CẢ Desktop Agent. Hành động này sẽ tạo mới một API Token, khiến các Agent hiện tại bị mất kết nối ngay lập tức. Bạn có muốn tiếp tục?')) {
                        try {
                          const res = await fetch('/v1/billing/generate-token', {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${localStorage.getItem('eric_token')}` }
                          });
                          const data = await res.json();
                          if (res.ok && data.success) {
                            alert('Đã thu hồi thành công! API Token mới đã được tạo ra. Các kết nối cũ đã bị từ chối.');
                            window.location.reload();
                          } else {
                            alert('Lỗi khi thu hồi quyền truy cập: ' + (data.error || 'Unknown error'));
                          }
                        } catch (err) {
                          alert('Lỗi kết nối tới máy chủ!');
                        }
                      }
                    }}
                    style={{ background: 'rgba(239,68,68,0.1)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)', padding: '6px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, transition: 'all 0.2s' }}>
                    Revoke Agent
                  </button>
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(148,163,184,0.15)', borderRadius: '8px', flexWrap: 'wrap', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: 'rgba(16,185,129,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>🌐</div>
                <div>
                  <div style={{ fontWeight: 700, color: '#fff', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    Web Dashboard (mec.wowerix.com) <span style={{ fontSize: '11px', padding: '2px 6px', background: 'rgba(56,189,248,0.2)', color: '#38bdf8', borderRadius: '12px' }}>🔵 Master Controller</span>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>Permissions: Full Administrative & Billing Management</div>
                </div>
              </div>
              <span style={{ fontSize: '12px', color: '#4ade80', fontWeight: 600 }}>Current Session</span>
            </div>
          </div>
        </div>
      </div>

      {/* Remote Folder Browser Modal */}
      {showBrowser && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, backdropFilter: 'blur(4px)' }}>
          <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: '12px', width: '500px', maxWidth: '90%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5)' }}>
            
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #1e293b' }}>
              <h3 style={{ margin: 0, fontSize: '16px', color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FolderTree size={18} style={{ color: '#38bdf8' }} />
                Browse {selectedAgent}
              </h3>
              <button onClick={() => setShowBrowser(false)} style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px' }}>
                <X size={20} />
              </button>
            </div>

            {/* Path and Actions */}
            <div style={{ padding: '12px 20px', background: 'rgba(15,23,42,0.4)', display: 'flex', alignItems: 'center', gap: '10px', borderBottom: '1px solid #1e293b' }}>
              <button 
                onClick={() => {
                  if (browserPath.includes('\\')) {
                    const parts = browserPath.split('\\');
                    parts.pop();
                    const parent = parts.join('\\');
                    loadBrowserPath(parent || "");
                  } else {
                    loadBrowserPath(""); // back to root
                  }
                }}
                disabled={!browserPath}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px', background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.3)', borderRadius: '6px', color: browserPath ? '#38bdf8' : '#64748b', cursor: browserPath ? 'pointer' : 'not-allowed' }}
              >
                <ArrowLeft size={16} />
              </button>
              
              <div style={{ flex: 1, padding: '6px 12px', background: '#1e293b', borderRadius: '6px', fontSize: '13px', color: '#cbd5e1', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {browserPath || "My Computer (Root Drives)"}
              </div>
              
              {browserPath && (
                <button 
                  onClick={() => handleAddWorkspace(browserPath)}
                  style={{ background: '#10b981', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  Set Workspace
                </button>
              )}
            </div>

            {/* File List */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px', minHeight: '300px' }}>
              {loadingBrowser ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>Loading...</div>
              ) : browserItems.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#64748b', fontSize: '14px' }}>Empty folder or access denied</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '2px' }}>
                  {browserItems.map((item, i) => (
                    <div 
                      key={i}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 12px', borderRadius: '6px', transition: 'background 0.2s' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(56,189,248,0.1)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', flex: 1 }} onClick={() => item.isDir && loadBrowserPath(item.path)}>
                        {item.isDir ? <Folder size={18} style={{ color: '#fbbf24' }} /> : <Terminal size={18} style={{ color: '#94a3b8' }} />}
                        <span style={{ fontSize: '14px', color: '#e2e8f0', fontWeight: 500 }}>{item.name}</span>
                      </div>
                      {item.isDir && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleAddWorkspace(item.path); }}
                          style={{ background: 'rgba(16,185,129,0.1)', color: '#4ade80', border: '1px solid rgba(16,185,129,0.3)', padding: '4px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                        >
                          Chọn
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function DashboardPage() {
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/v1/billing/status', {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('eric_token')}` }
    })
      .then(res => {
        if (res.status === 401) {
          localStorage.removeItem('eric_token');
          localStorage.removeItem('eric_role');
          window.location.href = '/login';
          throw new Error('Unauthorized');
        }
        return res.json();
      })
      .then(data => {
        setStatus(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to fetch billing status', err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return <div style={{ color: 'var(--text-muted)' }}>Loading context metrics...</div>;
  }

  // Nếu API fail (ví dụ chưa bật Worker), fallback data
  const data = status || {
    tokens_used: 12300000,
    tokens_saved: 0,
    tokens_quota: 50000000,
    sessions_active: 0,
    sessions_quota: 10
  };

  const usagePercent = Math.min(100, Math.round((data.tokens_used / data.tokens_quota) * 100));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '1000px', margin: '0 auto' }}>
      <div>
        <h2 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '8px' }}>Dashboard Overview</h2>
        <p style={{ color: 'var(--text-secondary)' }}>Welcome back. Here's what your agents have been up to.</p>
      </div>

      {/* Quick Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
        
        <div className="glass-panel" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{ background: 'rgba(124,58,237,0.1)', padding: '8px', borderRadius: '8px', color: 'var(--accent-primary)' }}>
              <Database size={24} />
            </div>
            <h3 style={{ fontSize: '16px', fontWeight: 500, color: 'var(--text-secondary)' }}>Virtual Context Stored</h3>
          </div>
          <div style={{ fontSize: '32px', fontWeight: 700 }}>{(data.tokens_used / 1000000).toFixed(1)}M <span style={{ fontSize: '14px', color: 'var(--text-muted)', fontWeight: 400 }}>tokens</span></div>
          <div style={{ marginTop: '12px', background: 'rgba(255,255,255,0.05)', height: '6px', borderRadius: '3px', overflow: 'hidden' }}>
            <div style={{ background: 'var(--gradient-main)', width: `${usagePercent}%`, height: '100%' }}></div>
          </div>
          <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--text-muted)', textAlign: 'right' }}>{usagePercent}% of {(data.tokens_quota / 1000000).toFixed(0)}M Quota</div>
        </div>

        <div className="glass-panel" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{ background: 'rgba(6,182,212,0.1)', padding: '8px', borderRadius: '8px', color: 'var(--accent-cyan)' }}>
              <BrainCircuit size={24} />
            </div>
            <h3 style={{ fontSize: '16px', fontWeight: 500, color: 'var(--text-secondary)' }}>Tokens Saved (This Month)</h3>
          </div>
          <div style={{ fontSize: '32px', fontWeight: 700 }}>{data.tokens_saved.toLocaleString()}</div>
          <div style={{ marginTop: '8px', fontSize: '14px', color: 'var(--accent-green)' }}>
            ≈ ${(data.tokens_saved * 0.00003).toFixed(2)} saved on LLM API costs
          </div>
        </div>

        <div className="glass-panel" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{ background: 'rgba(16,185,129,0.1)', padding: '8px', borderRadius: '8px', color: 'var(--accent-green)' }}>
              <Activity size={24} />
            </div>
            <h3 style={{ fontSize: '16px', fontWeight: 500, color: 'var(--text-secondary)' }}>Active Agent Sessions</h3>
          </div>
          <div style={{ fontSize: '32px', fontWeight: 700 }}>{data.sessions_active} <span style={{ fontSize: '14px', color: 'var(--text-muted)', fontWeight: 400 }}>/ {data.sessions_quota}</span></div>
          
          <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px', padding: '16px 0' }}>
            No active sessions. Start an agent to see it here.
          </div>
        </div>

      </div>

      {/* Local ComputeUse Agent Pairing Panel */}
      <LocalAgentPairingCard />

      {/* Recent Activity */}
      <div className="glass-panel" style={{ padding: '24px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>Recent Memory Updates</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left', color: 'var(--text-secondary)', fontSize: '13px' }}>
              <th style={{ padding: '12px 0', fontWeight: 500 }}>Time</th>
              <th style={{ padding: '12px 0', fontWeight: 500 }}>Action</th>
              <th style={{ padding: '12px 0', fontWeight: 500 }}>Agent</th>
              <th style={{ padding: '12px 0', fontWeight: 500 }}>Status</th>
            </tr>
          </thead>
          <tbody style={{ fontSize: '14px' }}>
            <tr>
              <td colSpan={4} style={{ padding: '32px 0', textAlign: 'center', color: 'var(--text-muted)' }}>
                No recent memory updates. Connect your MCP agent to get started.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
